import * as React from "react";
import {useEffect, useState} from "react";
import {useSearchParams} from "react-router-dom";
import Shell from "../../../components/specific/Shell.tsx";
import {BackendApi} from "../../../api/api.ts";
import {fetchDocumentContentBlob} from "../../../api/fetchDocumentContent.ts";
import {User} from "../../../api/accessControl/User.ts";
import {Input} from "../../../components/general/Input.tsx";
import {PrimaryButton} from "../../../components/general/PrimaryButton.tsx";
import {RODeveloperDocument} from "./types.ts";
import {RODeveloperDocumentsTable} from "./components/RODeveloperDocumentsTable.tsx";
import {RODeveloperDocumentDrawer} from "./components/RODeveloperDocumentDrawer.tsx";
import {useDocumentNavigation} from "../../../hooks/useDocumentNavigation.ts";
import Dropdown, {Option} from "../../../components/general/Dropdown.tsx";
import {TFICDocument} from "../../../views/DocumentDrawer/types/Document.ts";
import Checkbox from "../../../components/general/Checkbox.tsx";
import {MagnifyingGlassIcon} from "@heroicons/react/24/outline";
import type {DocumentCategory, DocumentDirection, DocumentStatus} from "../../../generated/enums.ts";

const CATEGORY_OPTIONS: Option[] = [
    { value: 'all', label: 'All Categories' },
    { value: 'invoice', label: 'Invoice' },
    { value: 'pos_receipt', label: 'POS Receipt' },
    { value: 'other', label: 'Other' },
] satisfies Array<{ value: DocumentCategory | 'all'; label: string }>;

const DIRECTION_OPTIONS: Option[] = [
    { value: 'all', label: 'All Directions' },
    { value: 'purchase_invoice', label: 'Purchase Invoice' },
    { value: 'sales_invoice', label: 'Sales Invoice' },
] satisfies Array<{ value: DocumentDirection | 'all'; label: string }>;

const STATUS_OPTIONS: Option[] = [
    { value: 'all', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'running', label: 'Running' },
    { value: 'processed', label: 'Processed' },
    { value: 'error', label: 'Error' },
] satisfies Array<{ value: DocumentStatus | 'all'; label: string }>;

const NEEDS_REVIEW_OPTIONS: Option[] = [
    { value: 'all', label: 'All Review States' },
    { value: 'true', label: 'Needs Review' },
    { value: 'false', label: 'Does Not Need Review' },
];

export default function DocumentSearchPage({ currentUser }: { currentUser: User }) {

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [searchUuid, setSearchUuid] = useState<string>("");
    const [category, setCategory] = useState<string>('all');
    const [direction, setDirection] = useState<string>('all');
    const [status, setStatus] = useState<string>('all');
    const [needsReview, setNeedsReview] = useState<string>('all');
    const [reviewContext, setReviewContext] = useState<string>('all');
    const [partnerBusinessId, setPartnerBusinessId] = useState<string>('all');
    const [failedCheckOptions, setFailedCheckOptions] = useState<Option[]>([{ value: 'all', label: 'All Failed Checks' }]);
    const [partnerOptions, setPartnerOptions] = useState<Option[]>([{ value: 'all', label: 'All Partners' }]);
    const [includeDuplicates, setIncludeDuplicates] = useState<boolean>(false);
    const [includeDeleted, setIncludeDeleted] = useState<boolean>(false);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [documents, setDocuments] = useState<RODeveloperDocument[]>([]);
    const [fullDocument, setFullDocument] = useState<TFICDocument | null>(null);
    const [isFetchingDetails, setIsFetchingDetails] = useState<boolean>(false);

    const [searchParams] = useSearchParams();
    const documentIdFromUrl = searchParams.get('document_id');
    const api = new BackendApi(currentUser.getToken());

    useEffect(() => {
        api.RoDeveloper__getFailedChecks({}).then(response => {
            const options = (response as any[]).map(check => ({
                value: check.search_key,
                label: check.search_key
            }));
            setFailedCheckOptions([{ value: 'all', label: 'All Failed Checks' }, ...options]);
        }).catch(console.error);

        api.RoDeveloper__getPartners({}).then(response => {
            const options = (response as any[]).map(partner => ({
                value: partner.business_id,
                label: partner.name
            }));
            setPartnerOptions([{ value: 'all', label: 'All Partners' }, ...options]);
        }).catch(console.error);
    }, []);

    const {
        isDrawerOpen,
        selectedDocument,
        currentIndex,
        openDrawer,
        closeDrawer,
        handleNext,
        handlePrevious
    } = useDocumentNavigation(documents);

    const handleSearch = async (overrideUuid?: string) => {
        const uuidToSearch = overrideUuid || searchUuid;

        setIsSearching(true);
        try {
            const response = await api.RoDeveloper__getAllDocuments(
                uuidToSearch || null,
                direction === 'all' ? null : direction,
                category === 'all' ? null : category,
                status === 'all' ? null : status,
                needsReview === 'all' ? null : needsReview === 'true',
                reviewContext === 'all' ? null : reviewContext,
                partnerBusinessId === 'all' ? null : partnerBusinessId,
                includeDuplicates,
                includeDeleted
            );

            const mappedDocuments = response as RODeveloperDocument[];

            setDocuments(mappedDocuments);
        } catch (e) {
            console.error(e);
            setDocuments([]);
        } finally {
            setIsSearching(false);
        }
    };

    const fetchDocumentDetails = async (documentId: string) => {
        setIsFetchingDetails(true);
        try {
            const [response, blob] = await Promise.all([
                api.RoDeveloper__getDocument(documentId),
                fetchDocumentContentBlob(api, 'ro-developer/get-document-content', {
                    document_id: documentId,
                }),
            ]);
            if (response) {
                const fetchedDoc = {
                    ...(response),
                    id: documentId,
                    file_blob: blob,
                } as unknown as TFICDocument;

                setFullDocument(fetchedDoc);
            }
        } catch (e) {
            console.error("Failed to fetch document details", e);
        } finally {
            setIsFetchingDetails(false);
        }
    };

    useEffect(() => {
        if (isDrawerOpen && selectedDocument) {
            fetchDocumentDetails(selectedDocument.id);
        } else if (!isDrawerOpen) {
            setFullDocument(null);
        }
    }, [isDrawerOpen, selectedDocument?.id]);

    useEffect(() => {
        setIsLoading(false);
        if (documentIdFromUrl) {
            setSearchUuid(documentIdFromUrl);
            handleSearch(documentIdFromUrl);
        }
    }, [documentIdFromUrl]);

    return (
        <Shell currentUser={currentUser} isLoading={isLoading}>
            <div className="p-6">
                <div className="max-w-[1600px] mx-auto space-y-6">
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <h2 className="text-xl font-semibold mb-4 text-gray-800">Document Search</h2>
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                            <div className="md:col-span-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">UUID</label>
                                <Input
                                    placeholder="Paste document UUID here..."
                                    value={searchUuid}
                                    onChange={(e) => setSearchUuid(e.target.value)}
                                />
                            </div>
                            <div className="md:col-span-3">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                <Dropdown
                                    selected={CATEGORY_OPTIONS.find(o => o.value === category) || CATEGORY_OPTIONS[0]}
                                    options={CATEGORY_OPTIONS}
                                    onUpdateFunc={(val) => setCategory(String(val))}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Direction</label>
                                <Dropdown
                                    selected={DIRECTION_OPTIONS.find(o => o.value === direction) || DIRECTION_OPTIONS[0]}
                                    options={DIRECTION_OPTIONS}
                                    onUpdateFunc={(val) => setDirection(String(val))}
                                />
                            </div>
                            <div className="md:col-span-3">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                <Dropdown
                                    selected={STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0]}
                                    options={STATUS_OPTIONS}
                                    onUpdateFunc={(val) => setStatus(String(val))}
                                />
                            </div>

                            <div className="md:col-span-3">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Needs Review</label>
                                <Dropdown
                                    selected={NEEDS_REVIEW_OPTIONS.find(o => o.value === needsReview) || NEEDS_REVIEW_OPTIONS[0]}
                                    options={NEEDS_REVIEW_OPTIONS}
                                    onUpdateFunc={(val) => setNeedsReview(String(val))}
                                />
                            </div>

                            <div className="md:col-span-6">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Review Context (Failed Check)</label>
                                <Dropdown
                                    selected={failedCheckOptions.find(o => o.value === reviewContext) || failedCheckOptions[0]}
                                    options={failedCheckOptions}
                                    onUpdateFunc={(val) => setReviewContext(String(val))}
                                />
                            </div>

                            <div className="md:col-span-3">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Partner</label>
                                <Dropdown
                                    selected={partnerOptions.find(o => o.value === partnerBusinessId) || partnerOptions[0]}
                                    options={partnerOptions}
                                    onUpdateFunc={(val) => setPartnerBusinessId(String(val))}
                                />
                            </div>

                            <div className="md:col-span-9 flex flex-wrap gap-x-6 gap-y-2 pt-2">
                                <Checkbox
                                    value={includeDuplicates}
                                    onUpdateFunc={setIncludeDuplicates}
                                    label="Include duplicated documents"
                                />
                                <Checkbox
                                    value={includeDeleted}
                                    onUpdateFunc={setIncludeDeleted}
                                    label="Include deleted documents"
                                />
                            </div>

                            <div className="md:col-span-3 flex justify-end">
                                <PrimaryButton
                                    title={isSearching ? "Searching..." : "Search Documents"}
                                    onClick={() => handleSearch()}
                                    disabled={isSearching}
                                    className="w-full md:w-auto min-w-[160px]"
                                />
                            </div>
                        </div>
                    </div>

                    {documents.length > 0 ? (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                            <RODeveloperDocumentsTable
                                documents={documents}
                                onRowClick={(doc) => openDrawer(doc)}
                            />
                        </div>
                    ) : (
                        !isSearching && (
                            <div className="bg-white p-12 rounded-lg shadow-sm border border-gray-200 text-center flex flex-col items-center justify-center space-y-4">
                                <div className="bg-gray-50 p-4 rounded-full">
                                    <MagnifyingGlassIcon className="h-12 w-12 text-gray-400" />
                                </div>
                                <div className="max-w-md mx-auto">
                                    <h3 className="text-lg font-medium text-gray-900 mb-1">
                                        {searchUuid || category !== 'all' || direction !== 'all' || status !== 'all' || includeDuplicates || includeDeleted
                                            ? "No documents found"
                                            : "Search for documents"}
                                    </h3>
                                    <p className="text-gray-500">
                                        {searchUuid || category !== 'all' || direction !== 'all' || status !== 'all' || includeDuplicates || includeDeleted
                                            ? "We couldn't find any documents matching your current filters. Try adjusting your search criteria."
                                            : "Enter a document UUID or adjust the filters above to find documents in the system."}
                                    </p>
                                </div>
                            </div>
                        )
                    )}
                </div>
            </div>

            <RODeveloperDocumentDrawer
                isOpen={isDrawerOpen}
                onClose={closeDrawer}
                document={selectedDocument}
                fullDocument={fullDocument}
                isFetching={isFetchingDetails}
                onNext={handleNext}
                onPrevious={handlePrevious}
                currentIndex={currentIndex}
                totalDocuments={documents.length}
            />
        </Shell>
    );
}
